import { MapPin } from 'lucide-react';
import { useMemo } from 'react';
import type {
  FieldKind,
  FieldSpec
} from 'v5/core/entity-schema/entitySchema.types';
import { validateContactDetailsAsTyped } from 'v5/core/entity-values/codecs/contact-details.codec';
import { resourceCodecs } from 'v5/core/entity-values/codecs/registry';
import type {
  ResourceKinds,
  ValidationIssue
} from 'v5/core/entity-values/values.types';
import type { FieldKit, FormKit } from '../schemaForm.types';
import type { FieldComponent } from './fieldComponent.types';
import {
  type IssueMessage,
  useResourceIssueMessage
} from './resourceIssueMessage';
import { BooleanField } from './BooleanField';
import {
  type ContactDetailsFieldConfig,
  createContactDetailsField
} from './ContactDetailsField';
import { CurrencyField } from './CurrencyField';
import { DateField } from './DateField';
import { DateRangeField } from './DateRangeField';
import { DimensionField } from './DimensionField';
import { createEntityRefField, EntityRefConfig } from './EntityRefField';
import { createGroupsField, GroupsFieldConfig } from './GroupsField';
import { HeightField } from './HeightField';
import { createLocationField, type LocationFieldConfig } from './LocationField';
import { MeasurementField } from './MeasurementField';
import { MeasurementLegacyField } from './MeasurementLegacyField';
import { MultiSelectField } from './MultiSelectField';
import { NameField } from './NameField';
import {
  createNetsuiteMasterProjectField,
  NetsuiteMasterProjectFieldConfig
} from './NetsuiteMasterProjectField';
import { NumberField } from './NumberField';
import { PercentageField } from './PercentageField';
import { PronounsField } from './PronounsField';
import { RateField } from './RateField';
import { RateRowHeading } from './rate/RateRowHeading';
import { resourceSpecIcon } from 'v5/patterns/resource/resourceFieldIcons';
import { resourceValueText } from './resourceValueText';
import { SingleSelectField } from './SingleSelectField';
import { TagsField } from './TagsField';
import { TextField } from './TextField';
import { UrlField } from './UrlField';

/** Everything the kit cannot know: service-bound hooks and feature renderers. */
export type ResourceFormKitConfig = {
  contactDetails: ContactDetailsFieldConfig;
  locations: LocationFieldConfig;
  entityRef: EntityRefConfig;
  groups: GroupsFieldConfig;
  netsuite: NetsuiteMasterProjectFieldConfig;
};

/** One kind: its component, its codec's rules, the shared words. */
function kindKit<K extends FieldKind>(
  kind: K,
  Component: FieldComponent<K>,
  message: IssueMessage,
  rows: Pick<FieldKit<ResourceKinds, K>, 'RowHeading'> = {}
): FieldKit<ResourceKinds, K> {
  return {
    Component,
    validate: (spec, value) => resourceCodecs[kind].validate(value, spec),
    message,
    text: (value, spec) => resourceValueText[kind](value, spec),
    icon: resourceSpecIcon,
    isEmpty: (spec, value) => resourceCodecs[kind].isEmpty(value, spec),
    ...rows
  };
}

/** A number from the first keystroke in an empty line (a digit), else nothing to keep. */
function typedNumber(text: string): number | null {
  const value = Number(text);
  return text.trim() === '' || Number.isNaN(value) ? null : value;
}

/** Contact details saved as typed: only a blank row or a social without a platform blocks Save; the “+” adds from the header. */
function contactDetailsKit(
  config: ContactDetailsFieldConfig,
  message: IssueMessage
): FieldKit<ResourceKinds, 'contact-details'> {
  return {
    Component: createContactDetailsField(config),
    validate: (spec, value) => validateContactDetailsAsTyped(value, spec),
    message,
    text: resourceValueText['contact-details'],
    icon: resourceSpecIcon,
    isEmpty: (spec, value) =>
      resourceCodecs['contact-details'].isEmpty(value, spec),
    unitAdd: {
      label: (spec, t) => t(`SchemaForm.contact.add.${spec.detailType}`)
    }
  };
}

/**
 * The resource form's kit: a closed map over `FieldKind`, so a kind added
 * to the schema without a component here does not compile.
 */
export function createResourceFormKit(
  config: ResourceFormKitConfig,
  message: IssueMessage
): FormKit<ResourceKinds> {
  return {
    text: {
      ...kindKit('text', TextField, message),
      listRow: { isEmpty: value => value === '', fromTyped: text => text }
    },
    boolean: kindKit('boolean', BooleanField, message),
    number: {
      ...kindKit('number', NumberField, message),
      listRow: { isEmpty: value => value === null, fromTyped: typedNumber }
    },
    date: {
      ...kindKit('date', DateField, message),
      listRow: { isEmpty: value => value === null }
    },
    'date-range': kindKit('date-range', DateRangeField, message),
    'single-select': {
      ...kindKit('single-select', SingleSelectField, message),
      listRow: { isEmpty: value => value === null }
    },
    'multi-select': kindKit('multi-select', MultiSelectField, message),
    measurement: kindKit('measurement', MeasurementField, message),
    currency: kindKit('currency', CurrencyField, message),
    url: {
      ...kindKit('url', UrlField, message),
      listRow: { isEmpty: value => value === '', fromTyped: text => text }
    },
    percentage: {
      ...kindKit('percentage', PercentageField, message),
      listRow: { isEmpty: value => value === null, fromTyped: typedNumber }
    },
    height: kindKit('height', HeightField, message),
    dimension: kindKit('dimension', DimensionField, message),
    'measurement-legacy': kindKit(
      'measurement-legacy',
      MeasurementLegacyField,
      message
    ),
    'netsuite-master-project': kindKit(
      'netsuite-master-project',
      createNetsuiteMasterProjectField(config.netsuite),
      message
    ),
    name: kindKit('name', NameField, message),
    pronouns: kindKit('pronouns', PronounsField, message),
    'contact-details': contactDetailsKit(config.contactDetails, message),
    tags: kindKit('tags', TagsField, message),
    groups: kindKit('groups', createGroupsField(config.groups), message),
    location: {
      ...kindKit('location', createLocationField(config.locations), message),
      listRow: {
        isEmpty: value => value.name.trim() === '',
        fromTyped: name => ({ name, coordinates: null }),
        LeadIcon: MapPin,
        copyText: value => (value.name.trim() === '' ? null : value.name)
      }
    },
    rate: kindKit('rate', RateField, message, {
      RowHeading: RateRowHeading
    }),
    'entity-ref': {
      ...kindKit('entity-ref', createEntityRefField(config.entityRef), message),
      text: (value, spec) =>
        value === null
          ? null
          : (config.entityRef[spec.entity].nameOf?.(value) ?? null),
      listRow: { isEmpty: value => value === null }
    }
  };
}

/** The resource kit with its words in the user's language. */
export function useResourceFormKit(
  config: ResourceFormKitConfig
): FormKit<ResourceKinds> {
  const message = useResourceIssueMessage();
  return useMemo(
    () => createResourceFormKit(config, message),
    [config, message]
  );
}
